import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * A Date's LOCAL calendar date as `YYYY-MM-DD` (not UTC).
 *
 * Use this instead of `new Date().toISOString().split("T")[0]` for "today"
 * defaults and any date the user picks. `toISOString()` is UTC: for IST
 * (UTC+5:30) users, between local midnight and 05:30 it returns *yesterday*, so
 * an early-morning egg/mortality/feed entry silently logs against the wrong day
 * and list filters hide the just-saved row.
 */
export function toLocalISODate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Today's LOCAL calendar date as `YYYY-MM-DD`. See toLocalISODate. */
export const todayISO = (): string => toLocalISODate(new Date());

/**
 * A calendar date the way the app shows it: "08/10/2026". Takes a DATE column
 * as the API sends it ("2026-10-08" or "2026-10-08T00:00:00Z") and reads the
 * digits — never `new Date()`, which moves a midnight-UTC date by the
 * browser's offset. A timestamp (created_at) is an instant, not a day: pass
 * `toLocalISODate(new Date(ts))` so it lands on the farm's day.
 */
export function displayDate(iso: string | null | undefined): string {
  if (!iso) return "-";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/**
 * Format a number as Indian-locale rupees with the sign OUTSIDE the symbol
 * (e.g. -0.4 → "-₹0.40", not "₹-0.40"). `decimals` fixes the fraction digits.
 */
export function formatINR(n: number, decimals = 0): string {
  const sign = n < 0 ? "-" : "";
  return (
    sign +
    "₹" +
    Math.abs(n).toLocaleString("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
  );
}

/** ₹ with no decimals — for whole-rupee totals. */
export const inrWhole = (n: number): string => formatINR(n, 0);

/** ₹ with 2 decimals, or an em-dash for null — for per-egg / per-kg figures. */
export const inrOrDash = (n: number | null): string => (n === null ? "—" : formatINR(n, 2));

/**
 * Margin/egg from the paise-rounded realized and cost, then re-rounded — so it
 * always reconciles with the two 2-decimal figures shown beside it. Single source
 * of truth for the flock card and the comparison table (they must not diverge).
 */
export const flockMarginPerEgg = (realized: number, cost: number): number =>
  Math.round((Number(realized.toFixed(2)) - Number(cost.toFixed(2))) * 100) / 100;

/**
 * Signed ₹ value: "+₹0.94" / "-₹0.30" / "₹0.00" (neutral zero). Rounds to paise
 * first so a value that rounds to zero shows no misleading sign. Shared by the
 * flock card and comparison table so the signed glyph can't diverge.
 */
export const signedInr = (v: number): string => {
  const r = Math.round(v * 100) / 100;
  return (r > 0 ? "+" : r < 0 ? "-" : "") + formatINR(Math.abs(r), 2);
};

/**
 * Tailwind tone classes for a margin figure: positive green, negative red,
 * break-even (0) neutral. Shared by the flock card header and the cost-breakdown
 * panel so the same flock's margin can't be coloured two different ways.
 */
export const marginToneClass = (m: number): string =>
  m > 0
    ? "text-success-600 dark:text-success-500"
    : m < 0
      ? "text-error-600 dark:text-error-500"
      : "text-gray-600 dark:text-gray-300";

export interface TargetVariance {
  /** actual − target, rounded to the display precision (`decimals`), signed. */
  delta: number;
  /** Hit or beat the goal at display precision. Exactly on target counts as meeting it. */
  meets: boolean;
  /** Green when meeting the goal, red when missing it. */
  toneClass: string;
}

/**
 * Variance of an actual metric against an owner-set target. Direction-aware:
 * HDP% is a floor (`higherIsBetter = true`) so above target is good; cost/egg is
 * a ceiling (`higherIsBetter = false`) so above target is bad. Tone reflects
 * *good vs bad*, not the sign of the delta — so a `+₹0.10` cost overrun is red
 * while a `+3pt` HDP beat is green. Single source of the meets/tone decision so
 * the flock card, comparison table, and farm P&L can't colour the same gap
 * differently (mirrors `marginToneClass`).
 *
 * The delta is rounded to `decimals` (the precision it will be *displayed* at) so
 * meets/sign agree with what's on screen: a raw ₹0.003 overrun rounds to ₹0.00 and
 * reads as on-target (● neutral), not a misleading "▲ ₹0.00".
 *
 * Returns null when there's nothing to compare: actual missing, or target unset
 * (null / undefined / ≤ 0 — a hand-edited zero/negative target is treated as unset).
 */
export const targetVariance = (
  actual: number | null,
  target: number | null | undefined,
  higherIsBetter: boolean,
  decimals = 2,
): TargetVariance | null => {
  if (actual === null || target === null || target === undefined || target <= 0) return null;
  // Round BOTH operands to the display precision before differencing, so the gap is
  // the difference of the shown figures (e.g. 6.2 − 5.8), never a rounded raw diff
  // (6.2 − 5.75 = 0.45 → "0.5") that wouldn't foot against the displayed 5.8.
  const a = Number(actual.toFixed(decimals));
  const t = Number(target.toFixed(decimals));
  const delta = Number((a - t).toFixed(decimals));
  const meets = higherIsBetter ? delta >= 0 : delta <= 0;
  const toneClass = meets
    ? "text-success-600 dark:text-success-500"
    : "text-error-600 dark:text-error-500";
  return { delta, meets, toneClass };
};

import type { ApplyCreditResult } from "@/types/models";

// Human-readable summary of a bulk apply-credit action, mentioning cleared AND
// part-paid bills plus any leftover credit.
export function formatApplyResult(res: ApplyCreditResult): string {
  if (res.applied_amount <= 0) return "No open bills to apply the credit to.";
  const parts: string[] = [];
  if (res.cleared_count > 0) parts.push(`cleared ${res.cleared_count} ${res.cleared_count === 1 ? "bill" : "bills"}`);
  if (res.partial_count > 0) parts.push(`part-paid ${res.partial_count} ${res.partial_count === 1 ? "bill" : "bills"}`);
  const leftover = res.leftover_credit > 0 ? `, ₹${res.leftover_credit.toLocaleString("en-IN")} credit left` : "";
  return `Applied ₹${res.applied_amount.toLocaleString("en-IN")} — ${parts.join(", ")}${leftover}.`;
}
